import { create } from 'zustand';
import { Platform } from 'react-native';
import { requiresAiSharingConsent } from '@/lib/appDistribution';
import { getStorage } from './storage';

export const AI_SHARING_CONSENT_VERSION = 1;
const keyFor = id => `ai-sharing-consent:v${AI_SHARING_CONSENT_VERSION}:${id}`;
let loadGeneration = 0;
let pendingManualChoice = null;

export function cancelManualAiSharingConsent() {
  const pending = pendingManualChoice;
  pendingManualChoice = null;
  if (useAiSharingConsent.getState().promptAccountId) useAiSharingConsent.setState({ promptAccountId: null, promptHost: 'app' });
  pending?.resolve(false);
}

// Explicit model-picker interactions request the same account/device choice.
// Mac store consent is also enforced at outbound boundaries.
export function requestManualAiSharingConsent({ host = 'app' } = {}) {
  if (Platform.OS !== 'ios') return Promise.resolve(true);
  const accountId = require('./auth').useAuthStore.getState().user?.id;
  if (!accountId) return Promise.resolve(false);
  const saved = useAiSharingConsent.getState();
  if (saved.accountId === accountId && saved.allowed && !saved.loading) return Promise.resolve(true);
  if (pendingManualChoice?.accountId === accountId) return pendingManualChoice.promise;
  cancelManualAiSharingConsent();
  let resolve;
  const promise = new Promise(done => { resolve = done; });
  const pending = { accountId, promise, resolve };
  pendingManualChoice = pending;
  void (async () => {
    let state = useAiSharingConsent.getState();
    if (state.accountId !== accountId || state.loading) await state.load(accountId);
    if (pendingManualChoice !== pending) return;
    if (require('./auth').useAuthStore.getState().user?.id !== accountId) {
      cancelManualAiSharingConsent();
      return;
    }
    state = useAiSharingConsent.getState();
    if (state.allowed && !state.loading) {
      pendingManualChoice = null;
      resolve(true);
    } else useAiSharingConsent.setState({ promptAccountId: accountId, promptHost: host });
  })();
  return promise;
}

// Keep synchronous picker behavior on platforms without this contextual prompt.
export function withManualAiSharingConsent(action, options) {
  if (Platform.OS !== 'ios') return action();
  const accountId = require('./auth').useAuthStore.getState().user?.id;
  const state = useAiSharingConsent.getState();
  if (accountId && state.accountId === accountId && state.allowed && !state.loading) return action();
  return requestManualAiSharingConsent(options).then(allowed => {
    if (!allowed || require('./auth').useAuthStore.getState().user?.id !== accountId) return false;
    return action();
  });
}

// Account and device scoped, with no expiry. Relaunches, updates and signing
// back into the same account retain the choice. Missing/unreadable storage or
// a new disclosure version requires another choice. The same receipt covers
// explicit model choices and the Mac store outbound AI guards.
export const useAiSharingConsent = create((set, get) => ({
  accountId: null, allowed: false, loading: false, error: null, promptAccountId: null, promptHost: 'app',
  load: async accountId => {
    if (pendingManualChoice && pendingManualChoice.accountId !== accountId) cancelManualAiSharingConsent();
    const generation = ++loadGeneration;
    set({ accountId, allowed: false, loading: true, error: null });
    try {
      const raw = accountId ? await getStorage().getItem(keyFor(accountId)) : null;
      const receipt = raw ? JSON.parse(raw) : null;
      if (generation === loadGeneration) set({ loading: false, allowed: receipt?.version === AI_SHARING_CONSENT_VERSION && receipt?.accountId === accountId && receipt?.allowed === true });
    } catch (_) {
      if (generation === loadGeneration) set({ loading: false, allowed: false, error: 'Your saved choice could not be read. Please choose again.' });
    }
  },
  choose: async (accountId, allowed) => {
    if (!accountId || get().accountId !== accountId) throw new Error('Your account changed. Please try again.');
    // Invalidate any older storage read before persisting this explicit choice.
    ++loadGeneration;
    // Revocation takes effect even if device storage cannot save the receipt.
    // Only an affirmative choice waits for a successful durable write.
    if (!allowed) set({ allowed: false, loading: false });
    await getStorage().setItem(keyFor(accountId), JSON.stringify({ accountId, version: AI_SHARING_CONSENT_VERSION, allowed, decidedAt: new Date().toISOString() }));
    if (get().accountId === accountId) {
      set({ allowed, loading: false, error: null, promptAccountId: null, promptHost: 'app' });
      if (pendingManualChoice?.accountId === accountId) {
        const pending = pendingManualChoice;
        pendingManualChoice = null;
        pending.resolve(allowed);
      }
    }
  },
}));

export function hasAiSharingConsent(accountId) {
  if (!requiresAiSharingConsent()) return true;
  const state = useAiSharingConsent.getState();
  return !!accountId && state.accountId === accountId && !state.loading && state.allowed;
}

export function assertAiSharingConsent() {
  if (!requiresAiSharingConsent()) return;
  const accountId = require('./auth').useAuthStore.getState().user?.id;
  if (!hasAiSharingConsent(accountId)) {
    const error = new Error('Allow AI data sharing before starting an AI request. You can review your choice in Settings → General.');
    error.code = 'AI_SHARING_CONSENT_REQUIRED';
    throw error;
  }
}

// The screen gate prevents AI surfaces from mounting before consent. Guard
// requests as well, including queued socket sends and delayed/retried uploads.
export function requestNeedsAiSharing(url, method = 'GET') {
  if (/\/json\/watch\/sessions(?:\?|$)/.test(String(url))) return true;
  const path = String(url).replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  if (String(method).toUpperCase() === 'DELETE') return false;
  if (/\/(stop|end|cancel)$/.test(path)) return false;
  if (/^\/api\/voice\//.test(path)) return !/\/(status|voices|heartbeat)$/.test(path);
  if (/^\/json\/work\/(starter-prompts|project-starter-prompts)$/.test(path)) return true;
  if (path === '/json/inference-tasks/suggestions') return true;
  if (['/json/social-shares/verify', '/json/social-shares/suggestions'].includes(path)) return true;
  if (['GET', 'HEAD', 'OPTIONS'].includes(String(method).toUpperCase())) return false;
  if (/^\/json\/business\/workspaces\/[^/]+\/conversations\/[^/]+\/messages$/.test(path)) return true;
  if (/^\/json\/business\/workspaces\/[^/]+\/tasks(?:\/[^/]+\/resume)?$/.test(path)) return true;
  return /^\/json\/(tasks|work|task-schedules|inference-tasks|conversations|agents|files|tools|prompts|memory|maps|documents|spreadsheets|presentations|images|videos|music|account-onboarding)(\/|$)/.test(path);
}

export const AI_SOCKET_EVENTS = new Set(['send_prompt', 'regenerate_message', 'trigger_conversation_socket_inference', 'retrieve_conversation_title', 'continue_generation', 'fastAnswerNow']);
